/**
 * <kc-fabric-sheet> — the "all fabrics & colours" bottom sheet of the variant
 * picker (blocks/_kc-product-detail-variant-picker.liquid, mobile_swatch_limit).
 *
 * The sheet owns no variant state. A tile tap checks the matching colour radio
 * inside <product-detail-variation> and fires `change`, i.e. the same path a
 * tap on an inline swatch takes, so the price, gallery, URL and every
 * `variant:change` listener react exactly as they already do.
 *
 * That change re-renders the whole section. The sheet is marked
 * `data-preserve-on-rerender`, so product-detail-variation.js swaps a CLONE of
 * it (classes and aria-pressed included) into the new markup: it stays open
 * while the gallery behind it updates. A clone does not carry listeners or
 * scroll position, hence the delegated document listeners and scrollMemory.
 *
 * Speed: a colour change waits for a section fetch before anything moves. To
 * make the sofa follow the tap at once, the sheet
 *   - warms the section HTML of the colours on screen (open, tab switch) and
 *     of the tile under the finger (pointerdown), and
 *   - swaps the gallery's first photo to the variant's own image immediately,
 *     with the same srcset/sizes the re-render will use, so the bytes are
 *     shared and the re-render lands on an image that is already painted.
 */

const scrollMemory = new Map();
const MOBILE = window.matchMedia("(max-width: 768px)");
const PREFETCH_ON_SHOW = 8;
const GALLERY_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000];

class KcFabricSheet extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.addEventListener("click", (event) => this.#onClick(event));
    this.addEventListener("pointerdown", (event) => this.#onIntent(event), { passive: true });

    const body = this.#scroller;
    if (body) {
      const saved = scrollMemory.get(this.id);
      if (saved) body.scrollTop = saved;
      body.addEventListener(
        "scroll",
        () => scrollMemory.set(this.id, body.scrollTop),
        { passive: true }
      );
    }

    // A clone taken mid add-to-cart would keep the busy state forever.
    this.querySelectorAll("[data-sheet-atc][aria-busy]").forEach((b) => b.removeAttribute("aria-busy"));

    // Arriving already open means this is the re-render clone: no slide-in.
    if (this.classList.contains("is-open")) this.setAttribute("data-settled", "");
  }

  get #scroller() {
    return this.querySelector("[data-sheet-scroll]");
  }

  get #form() {
    return document.getElementById(this.dataset.formId);
  }

  get #variation() {
    return this.#form?.closest("product-detail-variation") || null;
  }

  get #radios() {
    const form = this.#form;
    if (!form) return [];
    return Array.from(
      form.querySelectorAll(
        `input[type="radio"][name="${CSS.escape(this.dataset.optionName || "")}"]`
      )
    );
  }

  get isOpen() {
    return this.classList.contains("is-open");
  }

  open() {
    // Sync with the live selection: the sheet may have been preserved through
    // re-renders triggered by the inline swatches.
    const checked = this.#radios.find((radio) => radio.checked);
    let group = this.dataset.group || "";
    if (checked) {
      this.#markSelected(checked.value);
      const tile = this.#tileFor(checked.value);
      if (tile) group = tile.dataset.swatchGroup;
    }
    this.#showGroup(group);

    this.dataset.returnY = String(window.scrollY);

    // Bring the gallery to the top of the screen and give the sheet the rest,
    // so every colour picked is visible on the sofa right above the sheet.
    const gallery = this.#gallery();
    let height = window.innerHeight * 0.55;
    if (gallery) {
      gallery.scrollIntoView({ block: "start", behavior: "instant" });
      const space = window.innerHeight - gallery.getBoundingClientRect().bottom;
      height = Math.min(
        Math.max(space, window.innerHeight * 0.45),
        window.innerHeight * 0.72
      );
    }
    this.style.setProperty("--kc-sheet-height", `${Math.round(height)}px`);

    this.removeAttribute("data-settled");
    this.classList.add("is-open");

    const selected = this.querySelector('.kc-fabric-sheet__swatch[aria-pressed="true"]:not([hidden])');
    const body = this.#scroller;
    if (body) {
      body.scrollTop = selected ? Math.max(0, selected.offsetTop - body.offsetTop - 16) : 0;
      scrollMemory.set(this.id, body.scrollTop);
    }
    this.querySelector(".kc-fabric-sheet__close")?.focus({ preventScroll: true });
    this.#prefetchVisible();
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove("is-open");
    this.removeAttribute("data-settled");
    const returnY = Number(this.dataset.returnY);
    if (!Number.isNaN(returnY)) window.scrollTo({ top: returnY, behavior: "instant" });

    // The opener may have been replaced by a re-render since; find the live one.
    const opener = document.querySelector(`.kc-fabric-more[aria-controls="${this.id}"]`);
    opener?.focus({ preventScroll: true });

    // "The customer has chosen a colour" — even when they kept the one that
    // was already selected. The sticky bar's reveal="after-choice" waits for it.
    this.dispatchEvent(new CustomEvent("kc-fabric-sheet:close", { bubbles: true }));
  }

  #gallery() {
    const section = this.closest(".shopify-section") || document;
    return (
      section.querySelector(".product-gallery__ar-wrapper") ||
      section.querySelector(".product-gallery-wrapper")
    );
  }

  /**
   * An inline swatch of this picker was tapped (the sheet is closed): show
   * the result where it happens. Swap the gallery photo at once and bring the
   * gallery into view, so the customer sees the sofa change instead of a
   * swatch ring moving.
   */
  revealChoice(value) {
    this.previewChoice(value);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.#gallery()?.scrollIntoView({ block: "start", behavior: reduced ? "instant" : "smooth" });
  }

  /** Warm `value`: its section and its photos, so a click finds them ready. */
  warmChoice(value) {
    const variant = this.#variantFor(value);
    if (!variant) return;
    this.#variation?.prefetchVariant(variant.id);
    this.#preloadPhoto(variant);
  }

  /** Swap the gallery photo for `value` at once and warm its section — no scroll. */
  previewChoice(value) {
    const variant = this.#variantFor(value);
    if (!variant) return;
    this.#variation?.prefetchVariant(variant.id);
    this.#swapGalleryPhoto(variant);
  }

  /**
   * Check the colour `value` and fire `change`, the same way a sheet tile
   * does (used by the fabric close-up's "Diese Farbe wählen").
   */
  selectValue(value) {
    this.#select(value);
  }

  /** The value the colour sheet would open the close-up on: the selected tile
      of the fabric tab on screen, or that tab's first colour. */
  get detailValue() {
    const tiles = Array.from(this.querySelectorAll(".kc-fabric-sheet__swatch:not([hidden])"));
    const pressed = tiles.find((tile) => tile.getAttribute("aria-pressed") === "true");
    return (pressed || tiles[0])?.dataset.value || null;
  }

  #onClick(event) {
    if (event.target.closest("[data-sheet-close]")) {
      this.close();
      return;
    }

    const atc = event.target.closest("[data-sheet-atc]");
    if (atc) {
      this.#addToCart(atc);
      return;
    }

    const tab = event.target.closest("[data-sheet-tab]");
    if (tab) {
      this.#showGroup(tab.dataset.sheetTab);
      const body = this.#scroller;
      if (body) body.scrollTop = 0;
      this.#prefetchVisible();
      return;
    }

    const tile = event.target.closest(".kc-fabric-sheet__swatch");
    if (tile) this.#select(tile.dataset.value);
  }

  // The finger is down on a tile: the click follows ~100ms later. Start the
  // section fetch and the photo download now.
  #onIntent(event) {
    const tile = event.target.closest(".kc-fabric-sheet__swatch");
    if (!tile) return;
    const variant = this.#variantFor(tile.dataset.value);
    if (!variant) return;
    this.#variation?.prefetchVariant(variant.id);
    this.#preloadPhoto(variant);
  }

  /**
   * Add to cart from the sheet. If a colour was just picked, its section
   * re-render may still be on the way — and with it the product form's
   * variant id — so wait for it first (at most 4s), then submit the page's
   * product form: the same submit the main button fires, which upsell.js
   * intercepts to add cover and gifts in one call. The sheet closes so the
   * cart drawer is in view.
   */
  async #addToCart(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    try {
      if (this._pendingVariantId) {
        await new Promise((resolve) => {
          const done = () => { document.removeEventListener("variant:rerendered", done); resolve(); };
          document.addEventListener("variant:rerendered", done);
          setTimeout(done, 4000);
        });
      }
      const form = document.getElementById(this.dataset.productFormId);
      if (!form) return;
      // After a re-render the sheet on the page is a clone, not this element.
      const live = document.getElementById(this.id) || this;
      if (typeof live.close === "function") live.close();
      if (typeof form.requestSubmit === "function") form.requestSubmit();
      else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    } finally {
      button.removeAttribute("aria-busy");
    }
  }

  #select(value) {
    const radio = this.#radios.find((input) => input.value === value);
    if (!radio) return;
    this.#markSelected(value);
    if (radio.checked) return;

    const variant = this.#variantFor(value);
    if (variant) {
      this.#variation?.prefetchVariant(variant.id);
      this.#swapGalleryPhoto(variant);
      // Until the re-render lands, the product form still holds the old
      // variant id; the sheet's add-to-cart waits for it.
      this._pendingVariantId = variant.id;
      document.addEventListener("variant:rerendered", () => { this._pendingVariantId = null; }, { once: true });
    }

    radio.checked = true;
    this.isDispatching = true;
    try {
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    } finally {
      this.isDispatching = false;
    }
  }

  #variantFor(value) {
    const variation = this.#variation;
    if (!variation || typeof variation.variantForOption !== "function") return null;
    return variation.variantForOption(this.dataset.optionName, value) || null;
  }

  #prefetchVisible() {
    const variation = this.#variation;
    if (!variation || typeof variation.prefetchVariant !== "function") return;
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ""))) return;

    Array.from(this.querySelectorAll(".kc-fabric-sheet__swatch:not([hidden])"))
      .filter((tile) => tile.getAttribute("aria-pressed") !== "true")
      .slice(0, PREFETCH_ON_SHOW)
      .forEach((tile) => {
        const variant = this.#variantFor(tile.dataset.value);
        if (variant) variation.prefetchVariant(variant.id);
      });
  }

  #photoUrl(variant) {
    const image = variant && variant.featured_image;
    const src = typeof image === "string" ? image : image && image.src;
    if (!src) return null;
    return src.replace(/([?&])width=\d+&?/, "$1").replace(/[?&]$/, "");
  }

  #srcset(base) {
    const join = base.includes("?") ? "&" : "?";
    return GALLERY_WIDTHS.map((w) => `${base}${join}width=${w} ${w}w`).join(", ");
  }

  #firstGalleryImage() {
    return this.#gallery()?.querySelector(
      ".product-gallery__media-list .product-gallery__media img"
    );
  }

  /**
   * The other photos this colour will show — slots 2-5 of the grid — from the
   * gallery's [data-kc-gallery-sets] list (instant_gallery). Returns
   * [{ img, base }] for each slot that is an image now and will be one after,
   * or [] when the page carries no list.
   */
  #gallerySlots(variant) {
    const gallery = this.#gallery();
    const data = gallery?.querySelector("[data-kc-gallery-sets]");
    if (!variant || !data) return [];
    let sets;
    try {
      sets = JSON.parse(data.textContent);
    } catch (e) {
      return [];
    }
    const paths = sets[variant.id] || [];
    const first = this.#firstGalleryImage();
    const src = first && (first.getAttribute("src") || "");
    const cut = src.indexOf("/files/");
    if (cut < 0) return [];
    const prefix = src.slice(0, cut + "/files/".length);
    const media = Array.from(gallery.querySelectorAll(".product-gallery__media-list .product-gallery__media"));
    return paths
      .map((path, i) => {
        const img = media[i + 1]?.querySelector("img");
        return path && img ? { img, base: prefix + path } : null;
      })
      .filter(Boolean);
  }

  #preloadPhoto(variant) {
    const base = this.#photoUrl(variant);
    const current = this.#firstGalleryImage();
    if (!base || !current) return;
    const probe = new Image();
    probe.sizes = current.getAttribute("sizes") || "100vw";
    probe.srcset = this.#srcset(base);
    // The rest of the grid too, so a click swaps five photos the browser has.
    this.#gallerySlots(variant).forEach(({ img, base: slotBase }) => {
      const warm = new Image();
      warm.sizes = img.getAttribute("sizes") || "100vw";
      warm.srcset = this.#srcset(slotBase);
    });
  }

  #swapGalleryPhoto(variant) {
    const base = this.#photoUrl(variant);
    const img = this.#firstGalleryImage();
    if (!base || !img) return;

    const section = this.closest(".shopify-section");
    section?.classList.add("kc-gallery-preswapped");
    document.addEventListener(
      "variant:rerendered",
      () => setTimeout(() => section?.classList.remove("kc-gallery-preswapped"), 400),
      { once: true }
    );

    const join = base.includes("?") ? "&" : "?";
    img.srcset = this.#srcset(base);
    img.src = `${base}${join}width=2000`;

    // Slots 2-5 with it, so the grid changes as one instead of the main photo
    // first and the rest when the re-render lands.
    this.#gallerySlots(variant).forEach(({ img: slotImg, base: slotBase }) => {
      const slotJoin = slotBase.includes("?") ? "&" : "?";
      slotImg.srcset = this.#srcset(slotBase);
      slotImg.src = `${slotBase}${slotJoin}width=2000`;
    });

    // The re-rendered gallery opens on its first photo; show that one now.
    const list = img.closest(".product-gallery__media-list");
    if (list && list.scrollLeft !== 0) list.scrollTo({ left: 0, behavior: "instant" });
  }

  #tileFor(value) {
    return Array.from(this.querySelectorAll(".kc-fabric-sheet__swatch")).find(
      (tile) => tile.dataset.value === value
    );
  }

  #markSelected(value) {
    let current = null;
    this.querySelectorAll(".kc-fabric-sheet__swatch").forEach((tile) => {
      const selected = tile.dataset.value === value;
      tile.setAttribute("aria-pressed", selected ? "true" : "false");
      if (selected) current = tile;
    });
    const label = this.querySelector("[data-sheet-current]");
    if (label && current) {
      label.textContent = [current.dataset.swatchGroup, current.dataset.swatchName]
        .filter(Boolean)
        .join(" · ");
    }
  }

  #showGroup(group) {
    if (!group) return;
    this.dataset.group = group;
    this.querySelectorAll("[data-sheet-tab]").forEach((tab) => {
      const active = tab.dataset.sheetTab === group;
      tab.setAttribute("aria-pressed", active ? "true" : "false");
      if (active) {
        const strip = tab.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          strip.scrollLeft = tab.offsetLeft - strip.offsetLeft - 20;
        }
      }
    });
    this.querySelectorAll(".kc-fabric-sheet__swatch").forEach((tile) => {
      tile.hidden = tile.dataset.swatchGroup !== group;
    });
  }
}

if (!customElements.get("kc-fabric-sheet")) {
  customElements.define("kc-fabric-sheet", KcFabricSheet);

  // Delegated once for the page: openers and sheets are both replaced on
  // every variant re-render.
  document.addEventListener("click", (event) => {
    const opener = event.target.closest('[aria-controls^="kc-fabric-sheet-"]');
    if (!opener) return;
    const sheet = document.getElementById(opener.getAttribute("aria-controls"));
    if (sheet && typeof sheet.open === "function") {
      event.preventDefault();
      sheet.open();
    }
  });

  // Colours picked outside the sheet (the inline swatches, and on desktop the
  // fabric tabs, which pick their fabric's first colour): swap the gallery
  // photo at once, like the sheet does, instead of waiting for the re-render.
  // On a phone a real tap also scrolls up to the gallery. The sheet's own
  // picks already did this and are skipped (isDispatching).
  document.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "radio") return;
    if (!input.closest(".kc-vp--sheet .variant-color")) return;
    const form = input.closest("product-detail-variation")?.querySelector("form");
    if (!form || !form.id) return;
    const sheet = document.querySelector(`kc-fabric-sheet[data-form-id="${CSS.escape(form.id)}"]`);
    if (!sheet || sheet.isDispatching || sheet.dataset.optionName !== input.name) return;
    if (!MOBILE.matches) {
      sheet.previewChoice(input.value);
    } else if (event.isTrusted && !sheet.isOpen) {
      sheet.revealChoice(input.value);
    }
  });

  // Desktop: the mouse reaching a colour warms its photos (all five with
  // instant_gallery), so the click swaps photos the browser already has.
  // Once per colour per page view.
  const warmed = new Set();
  document.addEventListener(
    "pointerover",
    (event) => {
      if (MOBILE.matches || event.pointerType === "touch") return;
      const item = event.target.closest?.(".kc-vp--sheet .variant-color");
      const input = item?.querySelector('input[type="radio"]');
      if (!input || input.checked || warmed.has(input.value)) return;
      const form = input.closest("product-detail-variation")?.querySelector("form");
      if (!form || !form.id) return;
      const sheet = document.querySelector(`kc-fabric-sheet[data-form-id="${CSS.escape(form.id)}"]`);
      if (!sheet || sheet.dataset.optionName !== input.name || typeof sheet.warmChoice !== "function") return;
      warmed.add(input.value);
      sheet.warmChoice(input.value);
    },
    { passive: true }
  );

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll("kc-fabric-sheet.is-open").forEach((sheet) => sheet.close());
  });

  // Rotating a phone to a desktop-wide layout: the sheet is a phone control.
  MOBILE.addEventListener("change", (event) => {
    if (event.matches) return;
    document.querySelectorAll("kc-fabric-sheet.is-open").forEach((sheet) => sheet.close());
  });
}
